"use client";

import { useRef } from "react";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";

export default function AnimatedLogo({ className }: { className?: string }) {
  const containerRef = useRef<SVGSVGElement>(null);
  const lettersRef = useRef<(SVGPathElement | null)[]>([]);

  // We use contextSafe to ensure these functions are bound to the GSAP context
  // allowing for proper cleanup if the component unmounts.
  const { contextSafe } = useGSAP({ scope: containerRef });

  const handleMouseEnter = contextSafe(() => {
    lettersRef.current.forEach((letter, index) => {
      if (!letter) return;

      const angle = index * 45 - 90;
      const distance = 10 + Math.random() * 40;
      const radians = (angle * Math.PI) / 180;
      const x = Math.cos(radians) * distance;
      const y = Math.sin(radians) * distance;

      gsap.to(letter, {
        x,
        y,
        duration: 0.5,
        ease: "power2.out",
        overwrite: "auto", // <--- CRITICAL FIX
      });
    });
  });

  const handleMouseLeave = contextSafe(() => {
    // Return to original position
    gsap.to(lettersRef.current.filter(Boolean), {
      x: 0,
      y: 0,
      duration: 1.4,
      ease: "elastic.out(1.5, 0.3)",
      overwrite: "auto", // <--- CRITICAL FIX
    });
  });

  // On mobile, touchend doesn't trigger mouseleave, so we need to manually reset
  const handleTouchEnd = contextSafe(() => {
    // Small delay to let the scatter animation start, then reset
    gsap.delayedCall(0.3, () => {
      gsap.to(lettersRef.current.filter(Boolean), {
        x: 0,
        y: 0,
        duration: 1.4,
        ease: "elastic.out(1.5, 0.3)",
        overwrite: "auto",
      });
    });
  });

  return (
    <svg
      ref={containerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchEnd={handleTouchEnd}
      width="70"
      height="52"
      viewBox="0 0 303 222"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={`logo cursor-pointer ${className || ""}`}
      style={{ overflow: "visible" }}
    >
      {/* M */}
      <path
        ref={(el) => {
          lettersRef.current[0] = el;
        }}
        d="M5.376 137L21.248 51.624H33.28L55.936 105.896L79.616 51.624H91.648L105.984 137H89.088L81.408 84.392L58.496 137H52.864L30.976 84.392L22.272 137H5.376Z"
        fill="white"
      />
      {/* O */}
      <path
        ref={(el) => {
          lettersRef.current[1] = el;
        }}
        d="M109.304 25.248C117.837 16.8853 128.803 12.704 142.2 12.704C155.597 12.704 166.52 16.8853 174.968 25.248C183.501 33.6107 187.768 44.32 187.768 57.376C187.768 70.432 183.501 81.1413 174.968 89.504C166.52 97.7813 155.597 101.92 142.2 101.92C128.803 101.92 117.837 97.7813 109.304 89.504C100.856 81.1413 96.632 70.432 96.632 57.376C96.632 44.32 100.856 33.6107 109.304 25.248ZM121.72 78.624C127.011 84.0853 133.837 86.816 142.2 86.816C150.563 86.816 157.389 84.0853 162.68 78.624C167.971 73.0773 170.616 65.9947 170.616 57.376C170.616 48.7573 167.928 41.6747 162.552 36.128C157.261 30.5813 150.477 27.808 142.2 27.808C133.923 27.808 127.096 30.5813 121.72 36.128C116.429 41.6747 113.784 48.7573 113.784 57.376C113.784 65.9947 116.429 73.0773 121.72 78.624Z"
        fill="white"
      />
      {/* M */}
      <path
        ref={(el) => {
          lettersRef.current[2] = el;
        }}
        d="M53.376 212L69.248 126.624H81.28L103.936 180.896L127.616 126.624H139.648L153.984 212H137.088L129.408 159.392L106.496 212H100.864L78.976 159.392L70.272 212H53.376Z"
        fill="white"
      />
      {/* I */}
      <path
        ref={(el) => {
          lettersRef.current[3] = el;
        }}
        d="M153.728 104.624H170.368V190H153.728V104.624Z"
        fill="white"
      />
      {/* C */}
      <path
        ref={(el) => {
          lettersRef.current[4] = el;
        }}
        d="M240.68 134.472V153.928C234.365 157.256 227.325 158.92 219.56 158.92C207.101 158.92 196.691 154.952 188.328 147.016C179.197 138.227 174.632 127.347 174.632 114.376C174.632 100.552 179.197 89.416 188.328 80.968C196.349 73.4587 206.803 69.704 219.688 69.704C227.197 69.704 234.195 71.4533 240.68 74.952V94.408C235.133 88.008 228.264 84.808 220.072 84.808C211.709 84.808 204.883 87.752 199.592 93.64C194.387 99.4427 191.784 106.44 191.784 114.632C191.784 123.592 194.685 130.717 200.488 136.008C206.291 141.213 213.032 143.816 220.712 143.816C228.733 143.816 235.389 140.701 240.68 134.472Z"
        fill="white"
      />
      {/* S */}
      <path
        ref={(el) => {
          lettersRef.current[5] = el;
        }}
        d="M291.416 103.688L282.712 115.08C279.043 110.216 274.392 107.784 268.76 107.784C264.664 107.784 261.677 108.808 259.8 110.856C257.923 112.819 256.984 115.123 256.984 117.768C256.984 120.072 257.88 121.992 259.672 123.528C261.037 124.808 264.451 126.429 269.912 128.392C278.36 131.293 284.12 134.152 287.192 136.968C292.056 141.32 294.488 147.293 294.488 154.888C294.488 163.251 291.715 170.035 286.168 175.24C280.707 180.36 273.581 182.92 264.792 182.92C252.504 182.92 242.691 177.544 235.352 166.792L246.232 156.424C248.109 160.264 250.712 163.123 254.04 165C257.368 166.877 260.739 167.816 264.152 167.816C267.992 167.816 271.149 166.749 273.624 164.616C276.099 162.397 277.336 159.539 277.336 156.04C277.336 153.053 276.227 150.493 274.008 148.36C271.96 146.483 267.565 144.392 260.824 142.088C254.509 139.955 249.773 137.48 246.616 134.664C242.093 130.739 239.832 125.448 239.832 118.792C239.832 111.88 242.179 105.992 246.872 101.128C251.565 96.1787 258.349 93.704 267.224 93.704C276.867 93.704 284.931 97.032 291.416 103.688Z"
        fill="white"
      />
    </svg>
  );
}
